import {createRouter, createMemoryHistory, RouteRecordRaw} from 'vue-router'

import ListenAgainView from '@/views/active/ListenAgainView.vue'
import ListenView from '@/views/active/ListenView.vue'
import ShareView from '@/views/active/ShareView.vue'
import SplashView from '@/views/active/SplashView.vue'
import TrainingIntroView from '@/views/active/TrainingIntroView.vue'
import TrainingView from '@/views/active/TrainingView.vue'
import VideoView from '@/views/active/VideoView.vue'
import ActiveView from '@/views/ActiveView.vue'
import AudioView from '@/views/active/AudioView.vue'
import TextDescriptionView from '@/views/active/TextDescriptionView.vue'
import AudioSelectionView from '@/views/active/AudioSelectionView.vue'
import AudioSelectionChoiceView from '@/views/active/AudioSelectionChoiceView.vue'
import ChartView from '@/views/active/ChartView.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'active',
    component: ActiveView,
    children: [
      {
        path: '/about',
        name: 'active.about',
        component: TextDescriptionView,
        meta: {
          contentKey: 'about'
        }
      },

      {
        path: '',
        name: 'active.splash',
        component: SplashView,
        meta: {
          preloadAssets: [{type: 'AUDIO', path: require('@/assets/audio/healthy.mp3')}]
        }
      },

      {
        path: 'chart',
        name: 'active.chart',
        component: ChartView,
        meta: {
          contentKey: 'chart'
        }
      },

      {
        path: 'healthy',
        name: 'active.alive',
        component: AudioView,
        meta: {
          contentKey: 'healthy',
          audioPath: require('@/assets/audio/healthy.mp3'),
          preloadAssets: [{type: 'AUDIO', path: require('@/assets/audio/damaged.mp3')}],
          nextRoute: {name: 'active.silence'}
        }
      },

      {
        path: 'damaged',
        name: 'active.silence',
        component: AudioView,
        meta: {
          contentKey: 'damaged',
          audioPath: require('@/assets/audio/damaged.mp3'),
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/steve_intro.mp4')}
          ],
          nextRoute: {name: 'active.intro'}
        }
      },

      {
        path: 'intro',
        name: 'active.intro',
        component: VideoView,
        meta: {
          videoSource: require('@/assets/video/steve_intro.mp4'),
          subtitlesKey: 'intro.subtitles',
          preloadAssets: [],
          nextRoute: {name: 'active.training-intro'}
        }
      },

      {
        path: 'training-intro',
        name: 'active.training-intro',
        component: TrainingIntroView,
        meta: {
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/spectrogram_training.mp4')}
          ]
        }
      },

      {
        path: 'training/:locationId/:step',
        name: 'active.training',
        component: TrainingView,
        props: true,
        meta: {
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/mary_intro_1.mp4')}
          ]
        }
      },

      {
        path: 'spectrogram-training',
        name: 'active.spectrogram-training',
        component: VideoView,
        meta: {
          videoSource: require('@/assets/video/spectrogram_training.mp4'),
          preloadAssets: [
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/1-optimized.mp3')},
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/2-optimized.mp3')},
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/3-optimized.mp3')},
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/4-optimized.mp3')},
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/5-optimized.mp3')},
            {type: 'AUDIO', path: require('@/assets/audio/locations/1/6-optimized.mp3')}
          ],
          subtitlesKey: 'spectrogram.subtitles',
          nextRoute: {name: 'active.training', params: {locationId: 1, step: 1}}
        }
      },

      {
        path: 'science-intro-intro',
        name: 'active.science-intro-intro',
        component: VideoView,
        meta: {
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/mary_intro_2.mp4')}
          ],
          videoSource: require('@/assets/video/mary_intro_1.mp4'),
          subtitlesKey: 'science_intro_intro.subtitles',
          nextRoute: {name: 'active.science-intro'}
        }
      },

      {
        path: 'science-intro',
        name: 'active.science-intro',
        component: VideoView,
        meta: {
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/spectrogram_intro.mp4')}
          ],
          videoSource: require('@/assets/video/mary_intro_2.mp4'),
          subtitlesKey: 'science_intro.subtitles',
          nextRoute: {name: 'active.science-spectrogram-intro'}
        }
      },

      {
        path: 'science-spectrogram-intro',
        name: 'active.science-spectrogram-intro',
        component: VideoView,
        meta: {
          videoSource: require('@/assets/video/spectrogram_intro.mp4'),
          subtitlesKey: 'spectrogram_intro.subtitles',
          nextRoute: {name: 'active.listen-selection-choice'}
        }
      },

      {
        path: 'listen-selection',
        name: 'active.listen-selection',
        component: AudioSelectionView
      },
      {
        path: 'listen-selection-choice',
        name: 'active.listen-selection-choice',
        component: AudioSelectionChoiceView
      },

      {
        path: 'listen-intro',
        name: 'active.listen-intro',
        component: ChartView
      },

      {
        path: 'listen',
        name: 'active.listen',
        component: ListenView,
        meta: {
          title: 'Listen'
        }
      },

      {
        path: 'listen-again',
        name: 'active.listen-again',
        component: ListenAgainView,
        props: (route) => ({
          audioFileName: route.query.audioFileName
        }),
        meta: {
          preloadAssets: [
            {type: 'VIDEO', path: require('@/assets/video/steve_outro.mp4')}
          ]
        }
      },

      {
        path: 'outro',
        name: 'active.outro',
        component: VideoView,
        meta: {
          videoSource: require('@/assets/video/steve_outro.mp4'),
          subtitlesKey: 'outro.subtitles',
          nextRoute: {name: 'active.share'}
        }
      },

      {
        path: 'share',
        name: 'active.share',
        component: ShareView
      }
    ]
  }
]

const router = createRouter({
  history: createMemoryHistory(),
  routes
})

router.beforeEach((to, from, next) => {
  if (to.name !== 'active.splash' && !from.name) {
    next({name: 'active.splash'})
  }

  next()
})

export default router
